import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';

import errorHandler from 'client/utils/error-handler';

const isErrorPermissionDenied = (error: Error & { code?: string }) => error.code === 'permission-denied';

type Props = RouteComponentProps;
type State = {
  hasError: boolean;
  isPermissionDenied: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
  state = {
    hasError: false,
    isPermissionDenied: false,
  };

  unlisten: () => void = null;

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, isPermissionDenied: isErrorPermissionDenied(error) };
  }

  componentDidMount() {
    // attempt to re-render without error when location changes
    const { history } = this.props;
    this.unlisten = history.listen(() => {
      const { hasError } = this.state;
      if (hasError) {
        this.setState({
          hasError: false,
        });
      }
    });
  }

  componentWillUnmount() {
    this.unlisten();
  }

  componentDidCatch(error: Error) {
    // TODO: should we report all errors?
    try {
      errorHandler.report(error);
    } catch (e) {
      console.error('Failed to log the error in ErrorBoundary: ', e);
    }
  }

  render() {
    const { hasError, isPermissionDenied } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <>
          <h1 className="text-center">Something went wrong</h1>
          <div
            style={{
              width: '282px',
              height: '300px',
              background: 'url(https://static.introwise.com/images/sad-slony-and-mouse.jpg) center/cover no-repeat',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
          {isPermissionDenied ? (
            <div className="text-center">
              <p>You should not have access here.</p>
            </div>
          ) : (
            <div className="text-center">
              <p>We will fix this soon. Retrying again may help.</p>
              <button
                className="btn btn-default btn-sm"
                onClick={() => {
                  window.location.reload(false);
                }}
                type="button"
              >
                Refresh page
              </button>
            </div>
          )}
        </>
      );
    }
    return children;
  }
}

export default withRouter(ErrorBoundary);
